import classNames from 'classnames'
import { Xmark as CancelIcon, Plus as AddIcon, WarningCircle } from 'iconoir-react'
import { isEqual } from 'lodash'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { FilterJoiner } from './FilterJoiner'
import { FilterOperation } from './FilterOperation'
import { FilterSelector } from './FilterSelector'
import { FilterValue } from './FilterValue'
import { Tooltip } from 'app/components/Tooltip'
import { FilterOperator, FilterableColumn } from 'app/filter.utils'

/**
 * @Prop attribute - the ES attribute to filter
 * @Prop displayName - the name to display in the UI
 * @Prop operator - the operator to use to filter in ES
 * @Prop displayOperator - the operator to display in the UI
 * @Prop value - the value to filter
 * @Prop customSearchParam - the search param to use in the URL if the term comes from the loader
 * @Prop nonEmpty - if the term should be excluded from the URL if the value is empty
 */
export type FilterType = {
  attribute?: string
  secondaryAttribute?: string
  displayName?: string
  operator?: FilterOperator
  displayOperator?: string
  value?: string | string[]
  secondaryValue?: string[]
  customSearchParam?: string | string[]
  nonEmpty?: boolean
  group?: string
}

/**
 * Empty term used to initialize filters
 */
export const emptyFilter: FilterType = { attribute: ``, operator: undefined, value: `` }

type Props = {
  columns?: FilterableColumn[]
  appliedFilters?: FilterType[]
  defaultJoin?: `and` | `or`
  useOrOption?: boolean
  onFilterChange: (filters: FilterType[]) => void
  onJoinChange: (join: `and` | `or`) => void
  onFilterRemove?: (index: number) => void
}
/**
 * This component is responsible for rendering the search filters which are divided (from left to right).
 * - FilterSelector
 * - FilterJoiner
 * - FilterOperation
 * - FilterValue.
 * @Prop table columns that can be filtered
 * @Prop appliedFilters. If there are some filters that should be applied by default
 * @Prop className. Additional classes to apply to the component
 * @Prop defaultJoin. `and` or `or` to join the filters
 * @Prop useOrOption. By default it is true; however if it is false, the `or` option will not be displayed
 * @Prop onFilterChange. Function to call when a filter is added or updated
 * @Prop onJoinChange. Function to call when the join is changed
 * @Prop onFilterRemove. Optional function to call when a filter is removed. Eg. to remove the filter from the URL
 * @returns
 */
export function FiltersContainer({
  columns,
  appliedFilters,
  defaultJoin,
  useOrOption,
  onFilterChange,
  onJoinChange,
  onFilterRemove,
}: Props): ReactElement {
  const [selectedFilters, setSelectedFilters] = useState<FilterType[] | undefined>(
    appliedFilters && appliedFilters?.length > 0 ? appliedFilters : [emptyFilter],
  )
  const [join, setJoin] = useState<`and` | `or`>(defaultJoin ?? `and`)
  const prevAppliedFilters = useRef(appliedFilters)
  useEffect(() => {
    // Skip sync if appliedFilters content hasn't actually changed (prevents resetting user-added rules on parent re-render)
    if (isEqual(prevAppliedFilters.current, appliedFilters)) return
    prevAppliedFilters.current = appliedFilters

    // Sync with appliedFilters when it changes from external source (apply or clear)
    const isAppliedEmpty =
      !appliedFilters?.length || (appliedFilters.length === 1 && appliedFilters[0].attribute === emptyFilter.attribute)

    if (isAppliedEmpty) {
      setSelectedFilters([emptyFilter])
    } else {
      setSelectedFilters(appliedFilters)
    }
  }, [appliedFilters])
  const handleJoinChange = (value: `and` | `or`) => {
    setJoin(value)
    onJoinChange(value)
  }

  const handleChange = (filters: FilterType[]) => {
    setSelectedFilters(filters)
    onFilterChange(filters)
  }

  const removeFilter = (index: number) => {
    let updatedFilters = [...(selectedFilters ?? [])]
    updatedFilters?.splice(index, 1)
    setSelectedFilters(updatedFilters)
    onFilterChange(updatedFilters)
  }

  let renderQueryBuilder = useCallback(
    (index: number = 0) => {
      let selectedColumn = columns?.find((filter) => filter.attribute === selectedFilters?.[index]?.attribute)

      return (
        <>
          <FilterJoiner useOrOption={useOrOption} index={index} join={join} onChange={handleJoinChange} />
          <FilterSelector
            columns={columns}
            index={index}
            selectedColumn={selectedColumn}
            filters={selectedFilters}
            onSelectorChange={handleChange}
          />
          <FilterOperation
            index={index}
            selectedColumn={selectedColumn}
            filters={selectedFilters}
            onOperationChange={handleChange}
          />
          <FilterValue
            index={index}
            selectedColumn={selectedColumn}
            filters={selectedFilters}
            onValueChange={handleChange}
          />
          {selectedColumn?.readOnly ? (
            <Tooltip message="Read-only">
              <WarningCircle height={20} width={20} className="text-default" />
            </Tooltip>
          ) : (
            <CancelIcon
              width={20}
              height={20}
              strokeWidth={1.5}
              className="visible cursor-pointer"
              onClick={() => {
                removeFilter(index)
                onFilterRemove?.(index)
              }}
            />
          )}
        </>
      )
    },
    [selectedFilters, join],
  )

  return (
    <div className="flex flex-col px-4 py-3">
      <div className="flex flex-col space-y-2">
        {selectedFilters?.map((filter, index) => (
          <div
            key={`${filter.attribute}-${index}`}
            className="justify-left flex items-center space-x-2 align-middle text-body-sm text-strong"
          >
            {renderQueryBuilder(index)}
          </div>
        ))}
      </div>
      <button
        type="button"
        className={classNames(
          `flex w-24 items-center space-x-1 pl-1 align-middle text-strong`,
          selectedFilters?.length ? `mt-1 py-3` : null,
        )}
        onClick={() => setSelectedFilters((prev) => [...(prev ?? []), emptyFilter])}
      >
        <AddIcon width={16} height={16} className="cursor-pointer" strokeWidth={1.5} />
        <span className="text-ui-xs">Add rule</span>
      </button>
    </div>
  )
}
